package com.downdogapp.client.controllers

import com.downdogapp.*
import com.downdogapp.client.*
import com.downdogapp.client.api.RecordTeaserOptionSelectedRequest
import com.downdogapp.client.api.SettingOption
import com.downdogapp.client.api.SongSample
import com.downdogapp.client.controllers.playback.PlaybackViewController
import com.downdogapp.client.controllers.start.StartViewController
import com.downdogapp.client.resources.Images
import com.downdogapp.client.singleton.App
import com.downdogapp.client.singleton.Network
import com.downdogapp.client.views.SelectorView

class SelectorViewController private constructor(val options: List<SelectorOption>,
                                                 private val onOptionSelected: (Int) -> Unit, // when SELECT button is pressed
                                                 private val initialSelectedId: Int,
                                                 val title: String?,
                                                 val subtitle: String?,
                                                 val typeId: Int?,
                                                 val onboardingScreenIndex: Int?,
                                                 val numOnboardingScreens: Int?) : ViewController() {

  // constructor with a specific sequence setting type
  // last 3 filled in only for onboarding, which adds top bar with back, progress dots, and skip
  constructor(settingTypeId: Int,
              onOptionSelected: (Int) -> Unit,
              initialSelectedId: Int = SequenceSettings.getSelectedId(settingTypeId)!!,
              title: String? = SequenceSettings.getSelectorTitle(settingTypeId),
              subtitle: String? = SequenceSettings.getSelectorSubtitle(settingTypeId),
              onboardingScreenIndex: Int? = null,
              numOnboardingScreens: Int? = null) : this(
    options = SequenceSettings.getOptions(settingTypeId)
      .map {
        SelectorOption.fromSequenceSettingOption(settingTypeId, it)
      },
    onOptionSelected = onOptionSelected,
    initialSelectedId = initialSelectedId,
    title = title,
    subtitle = subtitle,
    typeId = settingTypeId,
    onboardingScreenIndex = onboardingScreenIndex,
    numOnboardingScreens = numOnboardingScreens
  )

  // constructor without a type, e.g. journeys, goal setting
  constructor(options: List<SelectorOption>,
              onOptionSelected: (Int) -> Unit,
              initialSelectedId: Int,
              title: String? = null,
              subtitle: String? = null,
              onboardingScreenIndex: Int? = null,
              numOnboardingScreens: Int? = null) : this(
    options = options,
    onOptionSelected = onOptionSelected,
    initialSelectedId = initialSelectedId,
    title = title,
    subtitle = subtitle,
    typeId = null,
    onboardingScreenIndex = onboardingScreenIndex,
    numOnboardingScreens = numOnboardingScreens
  )

  var selectedId = initialSelectedId

  private var audioSampleMediaPlayer: MediaPlayer? = null
  private var audioSampleAttribution: SongSample? = null

  private var videoSampleMediaPlayer: MediaPlayer? = null

  val hasVideoOptions get() = options.any { it.videoUrl != null }
  val hasImageOptions get() = options.any { it.imageUrl != null }

  // this lets us push multiple Selector VCs in a row (onboarding)
  override val name = "${super.name}${onboardingScreenIndex ?: ""}"

  val audioIcon
    get() = if (audioSampleAttribution != null)
      if (isPlayingAudio) Images.SKIP_ICON_OUTLINE else null
    else if (isPlayingAudio) Images.VOLUME_ICON_BLACK else null

  val isOnboarding get() = onboardingScreenIndex != null

  val isPlayingAudio get() = audioSampleMediaPlayer?.isPlaying ?: false

  private val selectedTeaserIds = mutableSetOf<Int>()

  private val selectedOption
    get() = options.single { it.id == selectedId }

  val visibleOptions
    get() = options.filter {
      (!isOnboarding || !it.hideDuringOnboarding)
      && (selectedGroupLabel == null || it.groupLabel == selectedGroupLabel)
    }

  val groupLabels =
    options
      .mapToSet { it.groupLabel }
      .let { if (null in it) null else it.filterNotNull() }

  var displayingGroups = groupLabels != null; private set

  private var selectedGroupLabel = selectedOption.groupLabel

  override val view = SelectorView(this)

  init {
    check((onboardingScreenIndex == null) == (numOnboardingScreens == null))
    logEvent(
      "opened",
      mapOf(
        "selectorType" to (typeId?.toString() ?: "null"),
        "title" to (title ?: "null"),
        "subtitle" to (subtitle ?: "null"),
        "isOnboarding" to isOnboarding.toString()
      )
    )
  }

  override fun onViewBecameVisible() {
    typeId?.let {
      if (App.getViewController(PlaybackViewController::class) == null) {
        App.activatePlaybackSession(true)
      }
      MediaSampleManager.onPushedSelector(typeId)
      playAudioSample()
      if (displayingGroups) {
        audioSampleMediaPlayer?.pause()
      }
      displayVideoOrImageBackground(optionId = selectedId)
    }
  }

  fun onItemClicked(id: Int) {
    if (selectedId != id) {
      selectedId = id
      playAudioSample()
      displayVideoOrImageBackground(optionId = id)

      if (typeId != null && !selectedOption.teaser) {
        SequenceSettings.saveSetting(
          typeId = typeId,
          optionId = selectedId
        )
      }
    } else {
      if (audioSampleAttribution != null) {
        playAudioSample()
      } else {
        audioSampleMediaPlayer?.togglePlaying()
      }
    }
    view.refreshView()
  }

  fun onGroupLabelClicked(groupLabel: String) {
    displayingGroups = false
    selectedGroupLabel = groupLabel
    onItemClicked(if (selectedOption.groupLabel == groupLabel) selectedOption.id else visibleOptions.first().id)
  }

  fun onSkippedSong() {
    playAudioSample()
  }

  override fun onBackClicked() {
    if (groupLabels == null || displayingGroups) {
      if (isOnboarding) {
        audioSampleMediaPlayer?.pause()
        App.popViewController()
      } else {
        onSelectClicked()
      }
    } else {
      displayingGroups = true
      audioSampleMediaPlayer?.pause()
      view.refreshView()
    }
  }

  fun onSelectClicked() {
    audioSampleMediaPlayer?.pause()
    when {
      selectedOption.teaser -> {
        selectedTeaserIds.add(selectedOption.id)
        App.alert(message = MANIFEST.teaserOptionSelectedText)
      }

      else -> {
        if (selectedId != initialSelectedId
            && typeId != null
            && !isOnboarding) {
          SequenceSettings.saveSelectorToRecents(typeId)
        }
        if (typeId != null && options.any { it.teaser }) {
          Network.post(
            request = RecordTeaserOptionSelectedRequest(
              typeId = typeId,
              teaserValues = selectedTeaserIds.toList(),
              finalValue = selectedId
            )
          )
        }
        onOptionSelected(selectedId)
        if (!isOnboarding) {
          App.popViewController() // For multipage onboarding, stack view controllers, don't pop them
        }
      }
    }
  }

  fun onSkipClicked() {
    audioSampleMediaPlayer?.pause()
    if (typeId != null) {
      SequenceSettings.saveSetting(typeId, initialSelectedId)
    }
    onOptionSelected(initialSelectedId)
  }

  override fun onBackgrounded() {
    audioSampleMediaPlayer?.pause()
  }

  override fun onPopped() {
    typeId?.let {
      audioSampleMediaPlayer?.releasePlayer()
      audioSampleMediaPlayer = null
      MediaSampleManager.onPoppedSelector(typeId)
      if (App.getViewController(StartViewController::class) == null) {
        App.deactivatePlaybackSession()
      }
    }
  }

  override fun viewDidLayoutSubviews() {
    view.updateVideoFrame()
  }

  private fun playAudioSample() {
    audioSampleMediaPlayer?.releasePlayer()

    val (audioPlayer, sample) =
      typeId?.let { MediaSampleManager.getAudioPlayer(typeId, selectedId) }
      ?: (null to null)
    audioSampleMediaPlayer = audioPlayer?.apply {
      onFinishedPlaying {
        playAudioSample()
      }
    }
    audioSampleAttribution = sample
    audioSampleMediaPlayer?.play()
    view.setSongAttribution(audioSampleAttribution?.let { "${it.title} ${BULLET_UNICODE}︎ ${it.artist}" })
    view.refreshView()
  }

  private fun displayVideoOrImageBackground(optionId: Int) {
    typeId?.let {
      MediaSampleManager
        .getImage(typeId = it, optionId = optionId)
        ?.let { view.updateBackgroundImage(image = it) }

      videoSampleMediaPlayer?.let {
        if (it.isPlaying) {
          it.pause()
          it.seekToTime(Duration.ZERO)
        }
      }

      videoSampleMediaPlayer =
        MediaSampleManager
          .getVideoPlayer(typeId = it, optionId = optionId)
          ?.apply {
            view.backgroundVideoUpdated(player = this)
            play()
          }
    }
  }
}

data class SelectorOption(val id: Int,
                          val label: String,
                          val groupLabel: String? = null,
                          val helpText: String? = null,
                          val hasAudioSample: Boolean = false,
                          val hasSongAttribution: Boolean = false,
                          val videoUrl: String? = null,
                          val imageUrl: String? = null,
                          val teaser: Boolean = false,
                          val hideDuringOnboarding: Boolean = false) {

  companion object {

    fun fromSequenceSettingOption(typeId: Int, option: SettingOption): SelectorOption =
      SelectorOption(
        id = option.id,
        label = SequenceSettings.getOptionLabel(typeId, option),
        groupLabel = option.groupLabel,
        helpText = option.helpText,
        hasAudioSample = option.audioUrl != null || option.songSamples.isNotEmpty(),
        hasSongAttribution = option.songSamples.isNotEmpty(),
        teaser = option.teaser,
        videoUrl = option.videoUrl,
        imageUrl = option.imageUrl,
        hideDuringOnboarding = option.hideDuringOnboarding
      )
  }
}
